import {
  compose, divide, find, flatMap, forEach, get, getOr, includes, join, map, range, replace, size, some, toString,
} from 'lodash/fp';
import dayjs from 'dayjs';
import XLSX from 'xlsx';
import Excel from 'exceljs';
import { saveAs } from 'file-saver';
import formatPhoneNumber from './formatPhoneNumber';

const questionCols = ['J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
const relationshipMap = {
  father: '부',
  mother: '모',
  grand_father: '조부',
  grand_mother: '조모',
  etc: '기타',
};

export const exportToExcel = (
  data,
  options = {
    metadata: { fileName: '', sheetName: '', bookType: 'xlsx' },
    merge: false,
  },
) => {
  const source = [data.head, ...data.body];

  const worksheet = XLSX.utils.aoa_to_sheet(source);

  if (options.merge) {
    worksheet['!merges'] = compose(
      map(num => ({
        s: { r: 0, c: num * 2 },
        e: { r: 0, c: num * 2 + 1 },
      })),
      range(0),
      length => divide(length, 2),
      size,
    )(data.head);
  }

  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, options.metadata.sheetName);

  const output = XLSX.write(workbook, {
    type: 'array',
    bookType: options.metadata.bookType,
  });

  saveAs(
    new Blob([output], { type: 'application/octet-stream' }),
    options.metadata.fileName,
  );
};

export const exportIndividuals = async (survey, individuals, options) => {
  const workbook = new Excel.Workbook();

  const now = new Date();

  workbook.creator = options.creator ?? '';
  workbook.lastModifiedBy = options.lastModifiedBy ?? '';
  workbook.created = now;
  workbook.modified = now;

  workbook.views = [
    {
      x: 10000,
      y: 1000,
      width: 30000,
      height: 20000,
      firstSheet: 0,
      activeTab: 1,
      visibility: 'visible',
    },
  ];

  const worksheet = workbook.addWorksheet(options.sheetName);

  const defaultColumns = [
    {
      header: ['학생 정보', '학년'],
      key: 'grade',
      width: 10,
    },
    {
      header: [undefined, '반'],
      key: 'clazzroom',
      width: 10,
    },
    {
      header: [undefined, '번호'],
      key: 'clazzNumber',
      width: 10,
    },
    {
      header: [undefined, '사용자 그룹'],
      key: 'customGroup',
      width: 16,
    },
    {
      header: [undefined, '학생 이름'],
      key: 'name',
      width: 18,
    },
    {
      header: ['대표 보호자 정보', '대표 보호자 이름'],
      key: 'guardianName',
      width: 18,
    },
    {
      header: [undefined, '대표 보호자 관계'],
      key: 'relationship',
      width: 22,
    },
    {
      header: [undefined, '대표 보호자 휴대폰번호'],
      key: 'guardianPhoneNumber',
      width: 26,
    },
    {
      header: '응답 시간',
      key: 'responsedAt',
      width: 18,
    },
  ];

  const questions = get('questions', survey);
  const signatures = get('signatures', survey);

  worksheet.columns = [
    ...defaultColumns,
    ...flatMap(question => (some(option => option.type === 'TEXT', question.options)
      ? [
        {
          header: `질문${question.number}${some(option => option.type === 'TEXT', question.options) ? '(기타 의견)' : ''}${question.required ? '*' : ''}`,
          key: question.number,
          width: 13,
        },
        { header: '', key: `${question.number}_TEXT`, width: 13 },
      ]
      : {
        header: `질문${question.number}${some(option => option.type === 'TEXT', question.options) ? '(기타 의견)' : ''}${question.required ? '*' : ''}`,
        key: question.number,
        width: 13,
      }), questions),
    {
      header: '전자 서명',
      key: 'signature',
      width: 13,
    },
  ];

  worksheet.mergeCells('A1:E1');
  worksheet.mergeCells('F1:H1');
  worksheet.mergeCells('I1:I2');

  let colNumber = 0;

  forEach((question) => {
    if (some(option => option.type === 'TEXT', question.options)) {
      worksheet.mergeCells(`${questionCols[colNumber]}1:${questionCols[colNumber + 1]}2`);

      worksheet.getColumn(questionCols[colNumber]).border = { right: { style: 'thin' } };
      worksheet.getColumn(questionCols[colNumber + 1]).border = { right: { style: 'thin' } };

      worksheet.getCell(`${questionCols[colNumber]}2`).border = { right: { style: 'thin' }, bottom: { style: 'double' } };

      worksheet.getCell(`${questionCols[colNumber]}1`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };

      colNumber += 2;
    } else {
      worksheet.mergeCells(`${questionCols[colNumber]}1:${questionCols[colNumber]}2`);

      worksheet.getColumn(questionCols[colNumber]).border = { right: { style: 'thin' } };

      worksheet.getCell(`${questionCols[colNumber]}2`).border = { right: { style: 'thin' }, bottom: { style: 'double' } };

      worksheet.getCell(`${questionCols[colNumber]}1`).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFE0E0E0' },
      };

      colNumber += 1;
    }
  }, get('questions', survey));

  worksheet.getColumn(questionCols[colNumber]).border = { right: { style: 'thin' } };
  worksheet.getColumn(questionCols[colNumber]).alignment = { horizontal: 'center' };

  worksheet.mergeCells(`${questionCols[colNumber]}1:${questionCols[colNumber]}2`);

  worksheet.getCell(`${questionCols[colNumber]}2`).border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell(`${questionCols[colNumber]}1`).fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };

  const rows = map(individual => [
    getOr('-', 'grade', individual.target.clazzGroup),
    getOr('-', 'clazzName', individual.target.clazzGroup),
    individual.target.clazzNumber || '-',
    join(',', map(customGroup => customGroup.name, individual.target.customGroups)) || '-',
    individual.target.name,
    getOr('-', 'name', individual.target.primaryGuardian),
    relationshipMap[getOr('etc', 'relationship', individual.target.primaryGuardian)],
    formatPhoneNumber(getOr('', 'phoneNumber', individual.target.primaryGuardian)),
    dayjs(individual.updatedAt).format('YYYY.MM.DD. HH:mm') || '-',
    ...flatMap.convert({ cap: false })((answer, key) => {
      const q = find(question => toString(question.number) === key, get('questions', survey));

      if (q.type === 'SINGLE_SELECTION') {
        if (some(option => option.type === 'TEXT', q.options)) {
          if (includes('other:', answer)) {
            const otherResponseOption = find(option => option.type === 'TEXT', q.options);

            return [`${otherResponseOption.number}번(기타)`, replace(/other:/, '', answer) || '-'];
          }

          return [`${answer}번`, '-'];
        }

        return `${answer}번`;
      } if (q.type === 'MULTIPLE_SELECTION') {
        if (some(option => option.type === 'TEXT', q.options)) {
          if (some(includes('other:'), answer)) {
            const otherResponseOption = find(option => option.type === 'TEXT', q.options);

            return [compose(
              join(', '),
              map(a => (includes('other:', a) ? `${otherResponseOption.number}번(기타)` : `${a}번`)),
            )(answer) || '-', compose(
              replace(/other:/, ''),
              find(includes('other:')),
            )(answer) || '-'];
          }

          return [compose(
            join(', '),
            map(a => `${a}번`),
          )(answer) || '-', '-'];
        }

        return compose(
          join(', '),
          map(a => `${a}번`),
        )(answer) || '-';
      }

      return answer || '-';
    }, individual.answers),
    signatures ? 'O' : '-',
  ], individuals);

  worksheet.addRows(rows);

  worksheet.properties.outlineLevelCol = 2;
  worksheet.properties.defaultRowHeight = 15;
  worksheet.properties.tabColor = '#88aaff';

  worksheet.getRow(1).font = { bold: true };
  worksheet.getRow(2).font = { bold: true };
  worksheet.getRow(1).alignment = { horizontal: 'center' };
  worksheet.getRow(2).alignment = { horizontal: 'center' };
  worksheet.getColumn('A').border = { right: { style: 'thin' } };
  worksheet.getColumn('B').border = { right: { style: 'thin' } };
  worksheet.getColumn('C').border = { right: { style: 'thin' } };
  worksheet.getColumn('D').border = { right: { style: 'thin' } };
  worksheet.getColumn('E').border = { right: { style: 'thin' } };
  worksheet.getColumn('F').border = { right: { style: 'thin' } };
  worksheet.getColumn('G').border = { right: { style: 'thin' } };
  worksheet.getColumn('H').border = { right: { style: 'thin' } };
  worksheet.getColumn('I').border = { right: { style: 'thin' } };
  worksheet.getColumn('A').alignment = { horizontal: 'center' };
  worksheet.getColumn('B').alignment = { horizontal: 'center' };
  worksheet.getColumn('C').alignment = { horizontal: 'center' };
  worksheet.getColumn('D').alignment = { horizontal: 'center' };
  worksheet.getColumn('E').alignment = { horizontal: 'center' };
  worksheet.getColumn('F').alignment = { horizontal: 'center' };
  worksheet.getColumn('G').alignment = { horizontal: 'center' };
  worksheet.getColumn('H').alignment = { horizontal: 'center' };
  worksheet.getColumn('I').alignment = { horizontal: 'center' };
  worksheet.getCell('A1').border = { right: { style: 'thin' }, bottom: { style: 'thin' } };
  worksheet.getCell('F1').border = { right: { style: 'thin' }, bottom: { style: 'thin' } };
  worksheet.getCell('A2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('B2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('C2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('D2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('E2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('F2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('G2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('H2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('I2').border = { right: { style: 'thin' }, bottom: { style: 'double' } };
  worksheet.getCell('A1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('F1').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('A2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('B2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('C2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('D2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('E2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('F2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('G2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('H2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };
  worksheet.getCell('I2').fill = {
    type: 'pattern',
    pattern: 'solid',
    fgColor: { argb: 'FFE0E0E0' },
  };

  const buffer = await workbook.xlsx.writeBuffer();

  saveAs(
    new Blob([buffer], { type: 'application/octet-stream' }),
    options.fileName,
  );
};
